import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { DEFAULT_PRODUCTION_SPECIES, PRODUCTION_SPECIES_OPTIONS } from '../data/productionSpeciesPresets'
import { useLots } from '../hooks/useLots'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { getActiveTenantKey } from '../offline/tenant'
import type { FeedLog, MortalityLog } from '../types/production'
import {
  clearEntityBatchSyncQueueBlockers,
  retryEntityBatchSyncStatus,
  type EntityBatchSyncBlocker,
  type EntityBatchSyncStatus,
  type EntityMutationSyncState
} from '../utils/entityMutationSync'
import { feedLogApi } from '../utils/feedLogApi'
import { fishStockLogApi } from '../utils/fishStockLogApi'
import { replaceImportTableRowRawLines } from '../utils/importTable'
import { mortalityLogApi } from '../utils/mortalityLogApi'
import { weightSampleLogApi } from '../utils/weightSampleLogApi'
import {
  clearProductionUnitTableRowSyncQueueBlockers,
  enqueueProductionUnitTableRowsUpsert,
  loadProductionUnitTableRows,
  retryProductionUnitTableRowSyncStatus,
  saveProductionUnitTableRows
} from '../utils/productionUnitTable'
import {
  clearWaterTableRowSyncQueueBlockers,
  enqueueWaterRowsUpsert,
  loadWaterTableRows,
  retryWaterTableRowSyncStatus,
  saveWaterTableRows
} from '../utils/waterTable'
import {
  buildImportWorkspaceRemainingSession,
  buildImportWorkspaceSavePlan
} from './importWorkspace/importWorkspaceCommit'
import { synthesizeFishStockLogsFromImportedRows } from './importWorkspace/importWorkspaceFishStockSynthesis'
import { synthesizeMortalityLogsFromImportedRows } from './importWorkspace/importWorkspaceMortalitySynthesis'
import { synthesizeWeightSampleLogsFromImportedRows } from './importWorkspace/importWorkspaceWeightSampleSynthesis'
import {
  IMPORT_WORKSPACE_TARGET_OPTIONS,
  buildImportWorkspaceReviewNotice,
  buildImportWorkspaceReviewPath,
  buildImportWorkspaceSession,
  parseImportWorkspaceRouteState,
  toImportSourceFile,
  type ImportWorkspaceSessionSnapshot,
  type ImportWorkspaceTargetKey
} from './importWorkspace/importWorkspaceSession'
import {
  buildSkippedRowCorrectionDrafts,
  collectChangedSkippedRowCorrections,
  hasErrorIssues,
  type SkippedRowCorrection
} from './importWorkspace/importWorkspaceCorrectionDrafts'
import { findFarmByIdOrName } from '../utils/contextSelection'
import { ensureFarmList } from '../utils/farmWizard'
import { getTimeZoneFromFarm, resolveTimeZone } from '../utils/timeBucketing'

const buildImportSuccessMessage = (): string => 'Local import save completed.'

// T3-A-04 dual-write: synthesize FishStockLog events for every imported
// production row that carries a valid fishStock value. The direct
// ProductionRecord.fishStock write is unchanged; T3-A-07 retires that
// column and drops the direct write, leaving this synthesis as the only
// source of truth. Only imported rows (syncRowIds) are considered —
// nextRows also includes existing rows already in the table which
// weren't part of this import. Failures are logged and swallowed: the
// ProductionRecord.fishStock write has already succeeded and the events
// can be re-synthesized later.
const synthesizeImportedFishStockLogs = async (args: {
  context: { farmId: string; productionUnitId: string }
  nextRows: import('../utils/productionUnitTable').BiomassDataRow[]
  syncRowIds: string[]
}): Promise<void> => {
  try {
    const importedRowIds = new Set(args.syncRowIds)
    const importedRows = args.nextRows.filter((row) => importedRowIds.has(row.id))
    const synthesized = synthesizeFishStockLogsFromImportedRows(importedRows, args.context)
    if (synthesized.length > 0) {
      await fishStockLogApi.saveImportedLogs(synthesized)
    }
  } catch (error) {
    console.warn(
      '[ImportWorkspace] FishStockLog synthesis failed after production import; ProductionRecord.fishStock writes succeeded',
      error
    )
  }
}

// v2.1 finding (2026-07-09): mirror the FishStockLog synthesis for the
// two remaining biology streams retired by T3-A-07b (`weightGrams`,
// `dailyMortality`). Without these, `saveProductionUnitTableRows` writes
// the imported values to localStorage only — `toRecordPayload` strips them
// on outbox upsert and the next `refreshProductionUnitTableFromApi` pull
// blanks the cells. The overlays (`overlayWeightSamplesOnProductionRows`,
// `overlayMortalityLogsOnProductionRows`) then have no events to restore
// the imported values from. See
// [docs/architecture-concerns.md](../../docs/architecture-concerns.md)
// invariant I-2 (every write site of a Tier B cache field must also write
// its paired Tier A event stream).
const synthesizeImportedWeightSampleLogs = async (args: {
  context: { farmId: string; productionUnitId: string }
  nextRows: import('../utils/productionUnitTable').BiomassDataRow[]
  syncRowIds: string[]
}): Promise<void> => {
  try {
    const importedRowIds = new Set(args.syncRowIds)
    const importedRows = args.nextRows.filter((row) => importedRowIds.has(row.id))
    const synthesized = synthesizeWeightSampleLogsFromImportedRows(importedRows, args.context)
    if (synthesized.length > 0) {
      await weightSampleLogApi.saveImportedLogs(synthesized)
    }
  } catch (error) {
    console.warn(
      '[ImportWorkspace] WeightSampleLog synthesis failed after production import; imported weight cells may vanish on next API refresh',
      error
    )
  }
}

const synthesizeImportedMortalityLogs = async (args: {
  context: { farmId: string; productionUnitId: string }
  nextRows: import('../utils/productionUnitTable').BiomassDataRow[]
  syncRowIds: string[]
}): Promise<void> => {
  try {
    const importedRowIds = new Set(args.syncRowIds)
    const importedRows = args.nextRows.filter((row) => importedRowIds.has(row.id))
    const synthesized = synthesizeMortalityLogsFromImportedRows(importedRows, args.context)
    if (synthesized.length > 0) {
      await mortalityLogApi.saveImportedLogs(synthesized)
    }
  } catch (error) {
    console.warn(
      '[ImportWorkspace] MortalityLog synthesis failed after production import; imported dailyMortality cells may vanish on next API refresh',
      error
    )
  }
}

const getImportRowLabel = (target: ImportWorkspaceTargetKey): string => {
  if (target === 'water') return 'water log'
  if (target === 'feed') return 'feed log'
  if (target === 'mortality') return 'mortality log'
  return 'production row'
}

const buildCompletionSyncLabel = (completion: CompletionState): string => {
  if (completion.queuedRows <= 0) return 'No queued sync'
  if (completion.blockedRows > 0) return 'Sync blocked'
  if (completion.syncState === 'offline') return 'Saved offline'
  if (completion.syncState === 'synced') return 'Synced'
  return 'Queued for sync'
}

const buildCompletionSyncValue = (completion: CompletionState): number => {
  if (completion.blockedRows > 0) return completion.blockedRows
  return completion.queuedRows
}

const buildImportActionLabel = (args: {
  isImportingRows: boolean
  readyRowCount: number
}): string => {
  if (args.isImportingRows) return 'Importing rows…'
  return `Import ${args.readyRowCount} ready row${args.readyRowCount === 1 ? '' : 's'}`
}

const buildSessionCommitHelpText = (target: ImportWorkspaceTargetKey | null): string => {
  if (target === 'feed') {
    return 'Commit only the rows without error issues. Feed saves imported logs through the canonical FeedLog boundary and keeps sync scoped to the new Feed rows.'
  }

  if (target === 'mortality') {
    return 'Commit only the rows without error issues. Mortality saves imported logs through the canonical MortalityLog boundary and keeps sync scoped to the new Mortality rows before returning to Biomass review.'
  }

  return 'Commit only the rows without error issues. Water replaces the current table snapshot; production summary prepends imported rows to the existing table and keeps sync scoped to those new rows.'
}

const buildReviewLabel = (target: ImportWorkspaceTargetKey): string => {
  if (target === 'water') return 'Review in Water'
  if (target === 'feed') return 'Review in Feed'
  if (target === 'mortality') return 'Review in Biomass'
  return 'Review in Production summary'
}

const retryImportWorkspaceSync = (target: ImportWorkspaceTargetKey, rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  if (target === 'water') {
    return retryWaterTableRowSyncStatus(rowIds)
  }

  if (target === 'feed') {
    return retryEntityBatchSyncStatus({
      entityType: 'feedLog',
      entityIds: rowIds,
      maxEntries: 100,
      maxPasses: 5
    })
  }

  if (target === 'mortality') {
    return retryEntityBatchSyncStatus({
      entityType: 'mortalityLog',
      entityIds: rowIds,
      maxEntries: 100,
      maxPasses: 5
    })
  }

  return retryProductionUnitTableRowSyncStatus(rowIds)
}

const clearImportWorkspaceQueueBlockers = (target: ImportWorkspaceTargetKey, rowIds: string[]): Promise<EntityBatchSyncStatus> => {
  if (target === 'water') {
    return clearWaterTableRowSyncQueueBlockers(rowIds)
  }

  if (target === 'feed') {
    return clearEntityBatchSyncQueueBlockers({
      entityType: 'feedLog',
      entityIds: rowIds,
      maxEntries: 100,
      maxPasses: 5
    })
  }

  if (target === 'mortality') {
    return clearEntityBatchSyncQueueBlockers({
      entityType: 'mortalityLog',
      entityIds: rowIds,
      maxEntries: 100,
      maxPasses: 5
    })
  }

  return clearProductionUnitTableRowSyncQueueBlockers(rowIds)
}

type SessionOutputProps = {
  session: ImportWorkspaceSessionSnapshot | null
  canImportRows: boolean
  isImportingRows: boolean
  readyRowCount: number
  onImportRows: () => void
  onApplyRowCorrection: (sourceRowNumber: number, nextRawLine: string) => void
  onApplyAllRowCorrections: (corrections: SkippedRowCorrection[]) => void
}

type CompletionState = {
  message: string
  target: ImportWorkspaceTargetKey
  reviewLabel: string
  reviewPath: string
  importedRows: number
  queuedRows: number
  localOnlyRows: number
  skippedCount: number
  blockedRows: number
  syncErrorMessage: string | null
  syncState: 'offline' | 'pending' | 'synced' | null
  syncRowIds: string[]
  blockers: EntityBatchSyncBlocker[]
  syncLastResultLabel: string | null
  syncLastAttemptedAtIso: string | null
}

type CompletionNoticeProps = {
  completion: CompletionState | null
  onReviewTarget: () => void
  onRetrySync: () => void
  onClearQueueBlockers: () => void
  isRetryingSync: boolean
  isClearingQueueBlockers: boolean
}

type BatchSyncResult = {
  syncState: EntityMutationSyncState | null
  blockedRows: number
  syncErrorMessage: string | null
  blockers: EntityBatchSyncBlocker[]
  syncPromise: Promise<EntityBatchSyncStatus> | null
}

const buildBlockingSyncErrorTitle = (queueBlockerCount: number): string => {
  if (queueBlockerCount > 1) {
    return 'Blocking sync errors'
  }

  return 'Blocking sync error'
}

const buildQueueBlockerLine = (args: {
  blocker: EntityBatchSyncBlocker
  index: number
  hasMultiple: boolean
}): string => {
  let prefix = 'Queue blocker'
  if (args.hasMultiple) {
    prefix = `Queue blocker ${args.index + 1}`
  }

  return `${prefix}: ${args.blocker.entityType} ${args.blocker.entityId}`
}

const buildClearQueueBlockerLabel = (args: {
  isClearing: boolean
  hasMultiple: boolean
}): string => {
  const suffix = args.hasMultiple ? 's' : ''
  if (args.isClearing) {
    return `Clearing blocker${suffix}…`
  }

  return `Clear queue blocker${suffix}`
}

const buildRetrySyncLabel = (isRetrying: boolean): string => {
  if (isRetrying) {
    return 'Retrying sync…'
  }

  return 'Retry queued sync'
}

const buildCompletionFollowUpMessage = (completion: CompletionState): string => {
  if (completion.skippedCount > 0) {
    return 'Skipped rows remain below for correction.'
  }

  if (completion.blockedRows > 0) {
    const queueBlockerCount = completion.blockers.filter((blocker) => blocker.scope === 'queue').length
    if (queueBlockerCount > 1) {
      return 'The current blocking outbox entries are shown above so the blocked rows can be corrected before retry.'
    }

    return 'The current blocking outbox error is shown above so the blocked rows can be corrected before retry.'
  }

  return 'Open the target screen to review the imported rows in context.'
}

const buildCompletionSyncResultLabel = (syncStatus: EntityBatchSyncStatus): string | null => {
  if (syncStatus.blockedCount > 0) {
    return syncStatus.errorMessage ? `Blocked: ${syncStatus.errorMessage}` : 'Blocked'
  }

  if (syncStatus.syncState === 'synced') {
    return 'Import sync is now caught up.'
  }

  if (syncStatus.syncState === 'offline') {
    return 'Import changes are local only while this device is offline.'
  }

  if (syncStatus.syncState === 'pending') {
    return 'Import changes are still pending sync.'
  }

  return null
}

function QueueBlockerList(props: Readonly<{ blockers: EntityBatchSyncBlocker[] }>) {
  if (props.blockers.length === 0) return null

  const hasMultiple = props.blockers.length > 1

  return (
    <div style={{ display: 'grid', gap: 4, marginTop: 6, fontSize: 12, color: '#7c2d12' }}>
      {props.blockers.map((blocker, index) => (
        <div key={`${blocker.entityType}-${blocker.entityId}-${blocker.seq ?? index}`}>
          {buildQueueBlockerLine({ blocker, index, hasMultiple })}
        </div>
      ))}
    </div>
  )
}

function CompletionSyncErrorNotice(props: Readonly<CompletionNoticeProps>) {
  if (!props.completion?.syncErrorMessage) return null

  const queueBlockers = props.completion.blockers.filter((blocker) => blocker.scope === 'queue')
  const hasQueueBlockers = queueBlockers.length > 0
  const hasMultipleQueueBlockers = queueBlockers.length > 1
  const canRetrySync = props.completion.queuedRows > 0 && props.completion.syncState !== 'synced'
  const actionDisabled = props.isRetryingSync || props.isClearingQueueBlockers
  const retryButtonBackground = actionDisabled ? '#cbd5e1' : '#9a3412'
  const retryButtonCursor = actionDisabled ? 'not-allowed' : 'pointer'
  const clearQueueBlockerLabel = buildClearQueueBlockerLabel({
    isClearing: props.isClearingQueueBlockers,
    hasMultiple: hasMultipleQueueBlockers
  })

  return (
    <div style={{ marginTop: 4, padding: 12, borderRadius: 12, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
      <div style={{ fontWeight: 700, marginBottom: 4 }}>{buildBlockingSyncErrorTitle(queueBlockers.length)}</div>
      <div>{props.completion.syncErrorMessage}</div>
      <QueueBlockerList blockers={queueBlockers} />
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        {canRetrySync ? (
          <button
            onClick={props.onRetrySync}
            disabled={actionDisabled}
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: 'none',
              background: retryButtonBackground,
              color: '#ffffff',
              fontWeight: 700,
              cursor: retryButtonCursor
            }}
          >
            {buildRetrySyncLabel(props.isRetryingSync)}
          </button>
        ) : null}
        {hasQueueBlockers ? (
          <button
            onClick={props.onClearQueueBlockers}
            disabled={actionDisabled}
            style={{
              padding: '10px 14px',
              borderRadius: 10,
              border: '1px solid #fdba74',
              background: '#ffffff',
              color: '#9a3412',
              fontWeight: 700,
              cursor: retryButtonCursor
            }}
          >
            {clearQueueBlockerLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ImportWorkspaceCompletionNotice(props: Readonly<CompletionNoticeProps>) {
  if (!props.completion) return null

  const reviewNotice = buildImportWorkspaceReviewNotice({
    rowLabel: getImportRowLabel(props.completion.target),
    importedRows: props.completion.importedRows,
    skippedRows: props.completion.skippedCount,
    queuedRows: props.completion.queuedRows,
    localOnlyRows: props.completion.localOnlyRows,
    blockedRows: props.completion.blockedRows,
    syncErrorMessage: props.completion.syncErrorMessage,
    syncState: props.completion.syncState
  })
  const followUpMessage = buildCompletionFollowUpMessage(props.completion)
  const lastSyncAttemptedAtLabel = props.completion.syncLastAttemptedAtIso
    ? new Date(props.completion.syncLastAttemptedAtIso).toLocaleString()
    : null

  return (
    <div style={{ ...cardStyle, marginBottom: 20, padding: 16, background: '#f0fdf4', borderColor: '#bbf7d0', color: '#166534' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ display: 'grid', gap: 6, maxWidth: 760 }}>
          <div>{props.completion.message}</div>
          {reviewNotice ? <div style={{ color: '#166534' }}>{reviewNotice}</div> : null}
          <div style={{ color: '#166534' }}>{followUpMessage}</div>
          {props.completion.syncLastResultLabel ? (
            <div style={{ padding: 12, borderRadius: 8, background: '#eff6ff', color: '#1d4ed8' }}>
              <div>Last sync result: {props.completion.syncLastResultLabel}</div>
              {lastSyncAttemptedAtLabel ? <div>Last attempted: {lastSyncAttemptedAtLabel}</div> : null}
            </div>
          ) : null}
          <CompletionSyncErrorNotice {...props} />
        </div>
        <button
          onClick={props.onReviewTarget}
          style={{
            padding: '12px 16px',
            borderRadius: 12,
            border: 'none',
            background: '#166534',
            color: '#ffffff',
            fontWeight: 700,
            cursor: 'pointer'
          }}
        >
          {props.completion.reviewLabel}
        </button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginTop: 14 }}>
        <div style={{ padding: 12, borderRadius: 12, background: '#ffffff', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Imported</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{props.completion.importedRows}</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: '#ffffff', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>{buildCompletionSyncLabel(props.completion)}</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{buildCompletionSyncValue(props.completion)}</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: '#ffffff', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Local only</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{props.completion.localOnlyRows}</div>
        </div>
        <div style={{ padding: 12, borderRadius: 12, background: '#ffffff', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase' }}>Skipped</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{props.completion.skippedCount}</div>
        </div>
      </div>
    </div>
  )
}

const buildCompletionStateWithSyncStatus = (args: {
  current: CompletionState
  syncStatus: EntityBatchSyncStatus
  farmId: string
  productionUnitId: string
  speciesKey?: string
  attemptedAtIso?: string
}): CompletionState => {
  const { current, syncStatus, farmId, productionUnitId, speciesKey, attemptedAtIso } = args

  return {
    ...current,
    blockedRows: syncStatus.blockedCount,
    syncErrorMessage: syncStatus.errorMessage,
    syncState: syncStatus.syncState,
    blockers: syncStatus.blockers,
    syncLastResultLabel: buildCompletionSyncResultLabel(syncStatus),
    syncLastAttemptedAtIso: attemptedAtIso ?? current.syncLastAttemptedAtIso,
    reviewPath: buildImportWorkspaceReviewPath({
      target: current.target,
      context: {
        farmId,
        productionUnitId,
        speciesKey
      },
      importedRows: current.importedRows,
      skippedRows: current.skippedCount,
      queuedRows: current.queuedRows,
      localOnlyRows: current.localOnlyRows,
      blockedRows: syncStatus.blockedCount,
      syncErrorMessage: syncStatus.errorMessage,
      syncState: syncStatus.syncState
    })
  }
}

function ImportWorkspaceSessionOutput(props: Readonly<SessionOutputProps>) {
  const [rowCorrectionDrafts, setRowCorrectionDrafts] = useState<Record<number, string>>(() => buildSkippedRowCorrectionDrafts(props.session))

  useEffect(() => {
    setRowCorrectionDrafts((current) => buildSkippedRowCorrectionDrafts(props.session, current))
  }, [props.session])

  if (!props.session) {
    return (
      <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
        Build a session to see the shared target, mapped headers, row results, and summary that the future dedicated import workspace will consume.
      </p>
    )
  }

  const importDisabled = !props.canImportRows || props.isImportingRows || props.readyRowCount === 0
  const importButtonLabel = buildImportActionLabel({
    isImportingRows: props.isImportingRows,
    readyRowCount: props.readyRowCount
  })
  const skippedResults = props.session.results.filter((result) => hasErrorIssues(result.issues))
  const changedSkippedRowCorrections = collectChangedSkippedRowCorrections(props.session, rowCorrectionDrafts)

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ color: '#475569', lineHeight: 1.6 }}>
          {buildSessionCommitHelpText(props.session.selectedTarget?.targetKey ?? null)}
        </div>
        <button
          onClick={props.onImportRows}
          disabled={importDisabled}
          style={{
            padding: '12px 16px',
            borderRadius: 12,
            border: 'none',
            background: importDisabled ? '#94a3b8' : '#15803d',
            color: '#ffffff',
            fontWeight: 700,
            cursor: importDisabled ? 'not-allowed' : 'pointer'
          }}
        >
          {importButtonLabel}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <div style={{ padding: 14, borderRadius: 12, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#1d4ed8' }}>Target</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{props.session.selectedTarget?.label ?? props.session.selectedTarget?.targetKey ?? '—'}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 12, background: '#ecfeff', border: '1px solid #a5f3fc' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#0f766e' }}>Rows</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{props.session.summary.totalRows}</div>
          <div style={{ fontSize: 13, color: '#155e75' }}>Valid: {props.session.summary.validRows} · Invalid: {props.session.summary.invalidRows}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 12, background: '#fefce8', border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#a16207' }}>Regenerated IDs</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{props.session.summary.regeneratedIdCount}</div>
          <div style={{ fontSize: 13, color: '#854d0e' }}>Warnings: {props.session.summary.rowsWithWarnings} · Errors: {props.session.summary.rowsWithErrors}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 12, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#15803d' }}>Outcome shell</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>{props.session.summary.outcomeCounts.ready}</div>
          <div style={{ fontSize: 13, color: '#166534' }}>Ready rows before save or confirm.</div>
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Mapped headers</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {props.session.mappedHeaders.filter((header) => header.targetField).map((header) => (
            <span key={`${header.sourceHeader}-${header.targetField}`} style={{ padding: '6px 10px', borderRadius: 999, background: '#e2e8f0', color: '#334155', fontSize: 13 }}>
              {header.sourceHeader} → {header.targetField}
            </span>
          ))}
        </div>
      </div>

      <div>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Issue groups</div>
        {props.session.summary.issueSummaries.length === 0 ? (
          <div style={{ color: '#475569' }}>No grouped issues. This session is clean enough to review or confirm.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {props.session.summary.issueSummaries.slice(0, 6).map((issue) => (
              <div key={`${issue.code}-${issue.targetField ?? 'none'}`} style={{ padding: 12, borderRadius: 12, background: issue.severity === 'error' ? '#fef2f2' : '#fff7ed', border: `1px solid ${issue.severity === 'error' ? '#fecaca' : '#fed7aa'}` }}>
                <div style={{ fontWeight: 700, color: '#0f172a' }}>{issue.code}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>
                  Severity: {issue.severity} · Count: {issue.count} · Rows: {issue.rowNumbers.join(', ')}
                </div>
                <div style={{ fontSize: 13, color: '#475569' }}>{issue.messages.join(' ')}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontWeight: 700, color: '#0f172a' }}>Skipped row corrections</div>
          {changedSkippedRowCorrections.length > 1 ? (
            <button
              onClick={() => props.onApplyAllRowCorrections(changedSkippedRowCorrections)}
              style={{
                padding: '10px 14px',
                borderRadius: 10,
                border: 'none',
                background: '#9a3412',
                color: '#ffffff',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              Apply {changedSkippedRowCorrections.length} changed corrections and rebuild
            </button>
          ) : null}
        </div>
        {skippedResults.length === 0 ? (
          <div style={{ color: '#475569' }}>No skipped rows are waiting for correction.</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {skippedResults.map((result) => {
              const sourceRowNumber = result.previewRow.sourceRowNumber
              const draftValue = rowCorrectionDrafts[sourceRowNumber] ?? result.previewRow.rawLine
              const rowIssues = result.issues
                .filter((issue) => issue.severity === 'error')
                .map((issue) => issue.message)

              return (
                <div key={`correction-${sourceRowNumber}`} style={{ padding: 14, borderRadius: 12, background: '#fff7ed', border: '1px solid #fed7aa' }}>
                  <div style={{ display: 'grid', gap: 6, marginBottom: 10 }}>
                    <strong style={{ color: '#9a3412' }}>Source row {sourceRowNumber}</strong>
                    <div style={{ fontSize: 13, color: '#9a3412' }}>{rowIssues.join(' ')}</div>
                    <div style={{ fontSize: 12, color: '#7c2d12' }}>
                      Edit the raw CSV row inline, then rebuild this session from the replacement line.
                      {changedSkippedRowCorrections.length > 1 ? ' Once multiple drafts are ready, you can rebuild them together with the batch action above.' : ''}
                    </div>
                  </div>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#7c2d12' }}>Raw CSV row</span>
                    <textarea
                      value={draftValue}
                      onChange={(event) => {
                        const nextValue = event.target.value
                        setRowCorrectionDrafts((current) => ({
                          ...current,
                          [sourceRowNumber]: nextValue
                        }))
                      }}
                      rows={3}
                      style={{ padding: 12, borderRadius: 12, border: '1px solid #fdba74', resize: 'vertical', fontFamily: 'Consolas, Monaco, monospace', background: '#ffffff', color: '#431407' }}
                    />
                  </label>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                    <button
                      onClick={() => props.onApplyRowCorrection(sourceRowNumber, draftValue)}
                      disabled={draftValue === result.previewRow.rawLine}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: 'none',
                        background: draftValue === result.previewRow.rawLine ? '#cbd5e1' : '#c2410c',
                        color: '#ffffff',
                        fontWeight: 700,
                        cursor: draftValue === result.previewRow.rawLine ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Apply correction and rebuild
                    </button>
                    <button
                      onClick={() => {
                        setRowCorrectionDrafts((current) => ({
                          ...current,
                          [sourceRowNumber]: result.previewRow.rawLine
                        }))
                      }}
                      disabled={draftValue === result.previewRow.rawLine}
                      style={{
                        padding: '10px 14px',
                        borderRadius: 10,
                        border: '1px solid #fdba74',
                        background: '#ffffff',
                        color: '#9a3412',
                        fontWeight: 700,
                        cursor: draftValue === result.previewRow.rawLine ? 'not-allowed' : 'pointer'
                      }}
                    >
                      Reset draft
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      <div>
        <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Row preview</div>
        <div style={{ display: 'grid', gap: 12 }}>
          {props.session.results.slice(0, 5).map((result) => (
            <div key={`${result.previewRow.sourceRowNumber}-${result.record.id ?? 'row'}`} style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 8 }}>
                <strong style={{ color: '#0f172a' }}>Source row {result.previewRow.sourceRowNumber}</strong>
                <span style={{ color: '#475569', fontSize: 13 }}>Issues: {result.issues.length} · Dedupe: {result.dedupeResult?.reason ?? 'none'}</span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {getRecordPreviewEntries(result.record).map(([key, value]) => (
                  <span key={`${result.previewRow.sourceRowNumber}-${key}`} style={{ padding: '6px 10px', borderRadius: 999, background: '#ffffff', border: '1px solid #cbd5e1', fontSize: 13, color: '#334155' }}>
                    {key}: {value}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#ffffff',
  border: '1px solid #d8e3f0',
  borderRadius: 16,
  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.08)'
}

// Fields worth seeing first in the row preview (id so the reader can tell
// rows apart, lot/lotId so a "which lot did this resolve to?" question like
// the one that prompted this fix never needs a DB query to answer) and
// fields worth pushing to the back (farmId/productionUnitId are identical
// across every row in a session — low signal for a per-row glance). Every
// other field keeps its natural record order. Without this, insertion
// order + a flat slice(0, 5) meant `lotId` reliably fell just past the cut
// because `lot` (its neighbor in the record) came first.
const PREVIEW_KEY_BOOST = ['id', 'lot', 'lotId']
const PREVIEW_KEY_DEFER = ['farmId', 'productionUnitId']
const PREVIEW_ENTRY_LIMIT = 6

const previewKeyRank = (key: string): number => {
  const boostIndex = PREVIEW_KEY_BOOST.indexOf(key)
  if (boostIndex !== -1) return boostIndex
  const deferIndex = PREVIEW_KEY_DEFER.indexOf(key)
  if (deferIndex !== -1) return PREVIEW_KEY_BOOST.length + 100 + deferIndex
  return PREVIEW_KEY_BOOST.length
}

export const getRecordPreviewEntries = (record: object): Array<[string, string]> => {
  return Object.entries(record as Record<string, unknown>)
    .filter(([, value]) => {
      if (value === null || value === undefined) return false
      if (typeof value === 'string') return value.trim().length > 0
      return typeof value === 'number' || typeof value === 'boolean'
    })
    .sort((a, b) => previewKeyRank(a[0]) - previewKeyRank(b[0]))
    .slice(0, PREVIEW_ENTRY_LIMIT)
    .map(([key, value]) => {
      if (typeof value === 'string') return [key, value] as [string, string]
      if (typeof value === 'number' || typeof value === 'boolean') return [key, String(value)] as [string, string]
      return [key, ''] as [string, string]
    })
}

export default function ImportWorkspace() {
  const navigate = useNavigate()
  const location = useLocation()
  const routeState = useMemo(() => parseImportWorkspaceRouteState(location.search), [location.search])
  const [tenantKey] = useState(() => getActiveTenantKey())
  const principal = useStoredPrincipal()
  const canImportRows = !principal || principal.role === 'editor' || principal.role === 'admin'
  const [target, setTarget] = useState<ImportWorkspaceTargetKey>('water')
  const [farmId, setFarmId] = useState('')
  const [productionUnitId, setProductionUnitId] = useState('')
  const [speciesKey, setSpeciesKey] = useState(DEFAULT_PRODUCTION_SPECIES)
  const [csvText, setCsvText] = useState('')
  const [sourceFile, setSourceFile] = useState<ReturnType<typeof toImportSourceFile> | null>(null)
  const [session, setSession] = useState<ImportWorkspaceSessionSnapshot | null>(null)
  const [feedRows, setFeedRows] = useState<FeedLog[]>([])
  const [mortalityRows, setMortalityRows] = useState<MortalityLog[]>([])
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [completionState, setCompletionState] = useState<CompletionState | null>(null)
  const [isReadingFile, setIsReadingFile] = useState(false)
  const [isImportingRows, setIsImportingRows] = useState(false)
  const [isRetryingCompletionSync, setIsRetryingCompletionSync] = useState(false)
  const [isClearingCompletionQueueBlockers, setIsClearingCompletionQueueBlockers] = useState(false)
  const [storageVersion, setStorageVersion] = useState(0)
  // v2.1 #47: keep an in-memory farm registry so we can resolve the farm
  // timezone for `(lot, day)` dedup keying. Browser TZ is used as a fallback
  // until the registry loads (or when the farm is unknown).
  const [availableFarms, setAvailableFarms] = useState<any[]>([])
  const completionSyncVersionRef = useRef(0)
  const skipNextSessionResetRef = useRef(false)

  const { lots, loading: lotsLoading, error: lotsError } = useLots({
    farmId,
    productionUnitId,
    enabled: Boolean(farmId && productionUnitId)
  })

  const waterRows = useMemo(() => {
    if (!farmId || !productionUnitId) return []
    return loadWaterTableRows({ farmId, productionUnitId })
  }, [farmId, productionUnitId, storageVersion])

  const productionRows = useMemo(() => {
    if (!farmId || !productionUnitId) return []
    return loadProductionUnitTableRows({ farmId, productionUnitId })
  }, [farmId, productionUnitId, storageVersion])

  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setFeedRows([])
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        const nextRows = await feedLogApi.list({ farmId, productionUnitId }, { preferLocal: true })
        if (!cancelled) {
          setFeedRows(nextRows)
        }
      } catch (error) {
        console.warn('[ImportWorkspace] Failed to load feed logs for import context', error)
        if (!cancelled) {
          setFeedRows([])
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, storageVersion])

  useEffect(() => {
    if (!farmId || !productionUnitId) {
      setMortalityRows([])
      return
    }

    let cancelled = false

    const load = async () => {
      try {
        const nextRows = await mortalityLogApi.list({ farmId, productionUnitId }, { preferLocal: true })
        if (!cancelled) {
          setMortalityRows(nextRows)
        }
      } catch (error) {
        console.warn('[ImportWorkspace] Failed to load mortality logs for import context', error)
        if (!cancelled) {
          setMortalityRows([])
        }
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [farmId, productionUnitId, storageVersion])

  useEffect(() => {
    // Findings #199: this older tool now imports Agua only (admins). Daily
    // records come in through the Hoja diaria, never as Base de datos rows.
    setTarget('water')
    setFarmId(routeState.farmId)
    setProductionUnitId(routeState.productionUnitId)
    setSpeciesKey(routeState.speciesKey)
    setCsvText('')
    setSourceFile(null)
    setSession(null)
    setErrorMessage(null)
    completionSyncVersionRef.current += 1
    setCompletionState(null)
    setIsRetryingCompletionSync(false)
    setIsClearingCompletionQueueBlockers(false)
  }, [routeState])

  // v2.1 #47: load the farm registry once so we can resolve the farm
  // timezone for dedup keying. Cheap, off-the-critical-path: the registry
  // ships from local storage / cached fetch via `ensureFarmList`.
  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const farms = await ensureFarmList()
        if (cancelled) return
        setAvailableFarms(farms)
      } catch {
        // Browser-TZ fallback in `buildSessionFromText` keeps preview usable.
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (skipNextSessionResetRef.current) {
      skipNextSessionResetRef.current = false
      return
    }

    setSession(null)
    setErrorMessage(null)
  }, [target, farmId, productionUnitId, speciesKey, csvText, sourceFile?.name, sourceFile?.lastModifiedMs, sourceFile?.sizeBytes])

  const buildSessionFromText = (nextText: string): ImportWorkspaceSessionSnapshot | null => {
    if (!farmId.trim() || !productionUnitId.trim()) return null

    const selectedFarm = findFarmByIdOrName(availableFarms, farmId.trim())
    const timeZone = resolveTimeZone(getTimeZoneFromFarm(selectedFarm))

    return buildImportWorkspaceSession({
      target,
      text: nextText,
      sourceFile,
      context: {
        farmId: farmId.trim(),
        productionUnitId: productionUnitId.trim(),
        speciesKey: target === 'productionDatatable' ? speciesKey : undefined
      },
      availableLots: lots.map((lot) => ({
        id: String(lot.id),
        lotKey: String(lot.lotKey ?? ''),
        stage: lot.stage
      })),
      waterRows,
      productionRows,
      feedRows,
      mortalityRows,
      timeZone
    })
  }

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    try {
      setIsReadingFile(true)
      const text = await file.text()
      setCsvText(text)
      setSourceFile(toImportSourceFile(file))
      setErrorMessage(null)
    } catch (error) {
      console.warn('[ImportWorkspace] Failed to read CSV file', error)
      setErrorMessage('Could not read the selected CSV file.')
    } finally {
      setIsReadingFile(false)
      event.target.value = ''
    }
  }

  const handleBuildSession = () => {
    completionSyncVersionRef.current += 1
    setCompletionState(null)

    if (!csvText.trim()) {
      setErrorMessage('Paste or upload a CSV file first.')
      return
    }
    if (!farmId.trim() || !productionUnitId.trim()) {
      setErrorMessage('Enter both Facility ID and Production Unit ID before building an import session.')
      return
    }

    const nextSession = buildSessionFromText(csvText)

    if (!nextSession) {
      setErrorMessage('The current CSV could not produce an import session for the selected target.')
      return
    }

    setSession(nextSession)
    setErrorMessage(null)
  }

  const applyRowCorrections = (corrections: SkippedRowCorrection[]) => {
    if (!session) {
      setErrorMessage('Build a session before correcting skipped rows.')
      return
    }

    if (corrections.length === 0) {
      setErrorMessage('Edit at least one skipped row before rebuilding the session.')
      return
    }

    const nextCsvText = replaceImportTableRowRawLines({
      table: session.table,
      replacements: corrections
    })
    const nextSession = buildSessionFromText(nextCsvText)

    if (!nextSession) {
      setErrorMessage(`The corrected row${corrections.length === 1 ? '' : 's'} could not be rebuilt into a valid import session.`)
      return
    }

    completionSyncVersionRef.current += 1
    setCompletionState(null)
    skipNextSessionResetRef.current = true
    setCsvText(nextCsvText)
    setSession(nextSession)
    setErrorMessage(null)
  }

  const handleApplyRowCorrection = (sourceRowNumber: number, nextRawLine: string) => {
    applyRowCorrections([{ sourceRowNumber, nextRawLine }])
  }

  const handleApplyAllRowCorrections = (corrections: SkippedRowCorrection[]) => {
    applyRowCorrections(corrections)
  }

  const savePlan = useMemo(() => {
    if (!session) return null
    return buildImportWorkspaceSavePlan({
      session,
      existingProductionRows: productionRows,
      existingWaterRows: waterRows,
      isOnline: typeof navigator !== 'undefined' && navigator.onLine
    })
  }, [productionRows, session, waterRows])

  const handleImportRows = async () => {
    if (!session) {
      setErrorMessage('Build a session before importing rows.')
      return
    }
    if (!savePlan) {
      setErrorMessage('Build a session before importing rows.')
      return
    }
    if (!canImportRows) {
      setErrorMessage('Read-only access: this token cannot import rows from the shared workspace.')
      return
    }
    if (!farmId.trim() || !productionUnitId.trim()) {
      setErrorMessage('Enter both Facility ID and Production Unit ID before importing rows.')
      return
    }
    if (savePlan.readyCount === 0) {
      setErrorMessage('No ready rows to import. Resolve the errors in this session first.')
      return
    }

    try {
      setIsImportingRows(true)
      const context = {
        farmId: farmId.trim(),
        productionUnitId: productionUnitId.trim()
      }
      const reviewSpeciesKey = target === 'productionDatatable' ? speciesKey : undefined
      const reviewPath = buildImportWorkspaceReviewPath({
        target: savePlan.target,
        context: {
          farmId: context.farmId,
          productionUnitId: context.productionUnitId,
          speciesKey: reviewSpeciesKey
        },
        importedRows: savePlan.readyCount,
        skippedRows: savePlan.skippedCount,
        queuedRows: savePlan.sync.queuedRows,
        localOnlyRows: savePlan.sync.localOnlyRows,
        blockedRows: 0,
        syncErrorMessage: null,
        syncState: savePlan.sync.syncState
      })

      let batchSyncResult: BatchSyncResult

      if (savePlan.target === 'water') {
        saveWaterTableRows(context, savePlan.nextRows, { skipSync: true })
        await enqueueWaterRowsUpsert(context, savePlan.nextRows, { skipAutoSync: true })
        batchSyncResult = buildBatchSyncResult({
          rowIds: savePlan.sync.queuedRowIds,
          retrySync: retryWaterTableRowSyncStatus
        })
      } else if (savePlan.target === 'feed') {
        await feedLogApi.saveImportedLogs(savePlan.nextRows)
        batchSyncResult = buildBatchSyncResult({
          rowIds: savePlan.sync.queuedRowIds,
          retrySync: (rowIds) => retryImportWorkspaceSync('feed', rowIds)
        })
      } else if (savePlan.target === 'mortality') {
        await mortalityLogApi.saveImportedLogs(savePlan.nextRows)
        batchSyncResult = buildBatchSyncResult({
          rowIds: savePlan.sync.queuedRowIds,
          retrySync: (rowIds) => retryImportWorkspaceSync('mortality', rowIds)
        })
      } else {
        saveProductionUnitTableRows(context, savePlan.nextRows, {
          skipSync: true,
          syncRowIds: savePlan.syncRowIds
        })
        await enqueueProductionUnitTableRowsUpsert(context, savePlan.nextRows, {
          syncRowIds: savePlan.syncRowIds,
          skipAutoSync: true
        })
        await synthesizeImportedFishStockLogs({
          context,
          nextRows: savePlan.nextRows,
          syncRowIds: savePlan.syncRowIds
        })
        await synthesizeImportedWeightSampleLogs({
          context,
          nextRows: savePlan.nextRows,
          syncRowIds: savePlan.syncRowIds
        })
        await synthesizeImportedMortalityLogs({
          context,
          nextRows: savePlan.nextRows,
          syncRowIds: savePlan.syncRowIds
        })
        batchSyncResult = buildBatchSyncResult({
          rowIds: savePlan.sync.queuedRowIds,
          retrySync: retryProductionUnitTableRowSyncStatus
        })
      }

      const remainingSession = buildImportWorkspaceRemainingSession(session)

      setStorageVersion((current) => current + 1)
      const completionVersion = completionSyncVersionRef.current + 1
      completionSyncVersionRef.current = completionVersion
      setCompletionState({
        message: buildImportSuccessMessage(),
        target: savePlan.target,
        reviewLabel: buildReviewLabel(savePlan.target),
        reviewPath,
        importedRows: savePlan.readyCount,
        queuedRows: savePlan.sync.queuedRows,
        localOnlyRows: savePlan.sync.localOnlyRows,
        skippedCount: savePlan.skippedCount,
        blockedRows: batchSyncResult.blockedRows,
        syncErrorMessage: batchSyncResult.syncErrorMessage,
        syncState: batchSyncResult.syncState,
        syncRowIds: savePlan.sync.queuedRowIds,
        blockers: batchSyncResult.blockers,
        syncLastResultLabel: batchSyncResult.syncState === null
          ? null
          : buildCompletionSyncResultLabel({
            syncState: batchSyncResult.syncState,
            blockedCount: batchSyncResult.blockedRows,
            errorMessage: batchSyncResult.syncErrorMessage,
            blockers: batchSyncResult.blockers
          }),
        syncLastAttemptedAtIso: new Date().toISOString()
      })
      if (batchSyncResult.syncPromise !== null) {
        void batchSyncResult.syncPromise.then((nextSyncStatus) => {
          if (completionSyncVersionRef.current !== completionVersion) return

          setCompletionState((current) => {
            if (!current) return current
            return buildCompletionStateWithSyncStatus({
              current,
              syncStatus: nextSyncStatus,
              farmId: context.farmId,
              productionUnitId: context.productionUnitId,
              speciesKey: reviewSpeciesKey
            })
          })
        })
      }
      setSession(remainingSession)
      if (!remainingSession) {
        setCsvText('')
        setSourceFile(null)
      }
      setErrorMessage(null)
    } catch (error) {
      console.warn('[ImportWorkspace] Failed to import rows', error)
      setErrorMessage('Could not import rows into the selected target.')
    } finally {
      setIsImportingRows(false)
    }
  }

  const handleReviewTarget = () => {
    if (!completionState) return
    navigate(completionState.reviewPath)
  }

  const handleRetryCompletionSync = async () => {
    if (!completionState || completionState.syncRowIds.length === 0) return

    const completionVersion = completionSyncVersionRef.current + 1
    completionSyncVersionRef.current = completionVersion

    try {
      setIsRetryingCompletionSync(true)

      const nextSyncStatus = await retryImportWorkspaceSync(completionState.target, completionState.syncRowIds)

      if (completionSyncVersionRef.current !== completionVersion) return

      setCompletionState((current) => {
        if (!current) return current
        return buildCompletionStateWithSyncStatus({
          current,
          syncStatus: nextSyncStatus,
          farmId: farmId.trim(),
          productionUnitId: productionUnitId.trim(),
          speciesKey: current.target === 'productionDatatable' ? speciesKey : undefined,
          attemptedAtIso: new Date().toISOString()
        })
      })
      setErrorMessage(null)
    } catch (error) {
      console.warn('[ImportWorkspace] Failed to retry queued sync', error)
      setErrorMessage('Could not retry queued sync from the import workspace.')
    } finally {
      setIsRetryingCompletionSync(false)
    }
  }

  const handleClearCompletionQueueBlockers = async () => {
    if (!completionState || completionState.syncRowIds.length === 0) return

    const completionVersion = completionSyncVersionRef.current + 1
    completionSyncVersionRef.current = completionVersion

    try {
      setIsClearingCompletionQueueBlockers(true)

      const nextSyncStatus = await clearImportWorkspaceQueueBlockers(completionState.target, completionState.syncRowIds)

      if (completionSyncVersionRef.current !== completionVersion) return

      setCompletionState((current) => {
        if (!current) return current
        return buildCompletionStateWithSyncStatus({
          current,
          syncStatus: nextSyncStatus,
          farmId: farmId.trim(),
          productionUnitId: productionUnitId.trim(),
          speciesKey: current.target === 'productionDatatable' ? speciesKey : undefined,
          attemptedAtIso: new Date().toISOString()
        })
      })
      setErrorMessage(null)
    } catch (error) {
      console.warn('[ImportWorkspace] Failed to clear queue blocker', error)
      setErrorMessage('Could not clear the blocking outbox entry from the import workspace.')
    } finally {
      setIsClearingCompletionQueueBlockers(false)
    }
  }

  let existingRowCount = productionRows.length
  if (target === 'water') {
    existingRowCount = waterRows.length
  } else if (target === 'feed') {
    existingRowCount = feedRows.length
  } else if (target === 'mortality') {
    existingRowCount = mortalityRows.length
  }
  const readyRowCount = savePlan?.readyCount ?? 0
  let csvFileStatusText = 'Optional if you prefer pasting CSV text below.'
  if (sourceFile?.name) csvFileStatusText = `Loaded ${sourceFile.name}`
  if (isReadingFile) csvFileStatusText = 'Reading file…'

  return (
    <div style={{ padding: '24px 0 48px' }}>
      <div style={{ ...cardStyle, padding: 24, background: 'linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%)', marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ maxWidth: 760 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#1d4ed8', marginBottom: 10 }}>
              Import Workspace
            </div>
            <h1 style={{ margin: '0 0 10px', fontSize: '2rem', color: '#0f172a' }}>
              Shared import-session shell
            </h1>
            <p style={{ margin: 0, color: '#475569', lineHeight: 1.6 }}>
              Build one workspace-ready session from CSV text, selected target, assigned context, real stored rows, and live lot context.
              This shell uses the shared Water, production-summary, Feed, and Mortality session builders instead of inventing new UI-local import state.
            </p>
          </div>
          <div style={{ minWidth: 240, padding: 16, borderRadius: 14, background: '#ffffff', border: '1px solid #cbd5e1' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Current shell status</div>
            <div style={{ fontSize: 14, color: '#334155', lineHeight: 1.6 }}>
              <div>Targets wired: Water, Production summary, Feed, Mortality</div>
              <div>Lots in current context: {lots.length}</div>
              <div>Existing rows in current target: {existingRowCount}</div>
              <div>CSV loaded: {sourceFile?.name ?? 'Paste only'}</div>
            </div>
          </div>
        </div>
      </div>

      <ImportWorkspaceCompletionNotice
        completion={completionState}
        onReviewTarget={handleReviewTarget}
        onRetrySync={handleRetryCompletionSync}
        onClearQueueBlockers={handleClearCompletionQueueBlockers}
        isRetryingSync={isRetryingCompletionSync}
        isClearingQueueBlockers={isClearingCompletionQueueBlockers}
      />

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 420px) minmax(0, 1fr)', gap: 20, alignItems: 'start' }}>
        <section style={{ ...cardStyle, padding: 20 }}>
          <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.2rem', color: '#0f172a' }}>Session inputs</h2>
          <div style={{ display: 'grid', gap: 14 }}>
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600, color: '#334155' }}>Target domain</span>
              <select value={target} onChange={(event) => setTarget(event.target.value as ImportWorkspaceTargetKey)} style={{ padding: 10, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                {IMPORT_WORKSPACE_TARGET_OPTIONS.filter((option) => option.key === 'water').map((option) => (
                  <option key={option.key} value={option.key}>{option.label}</option>
                ))}
              </select>
            </label>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600, color: '#334155' }}>Facility ID</span>
              <input value={farmId} onChange={(event) => setFarmId(event.target.value)} placeholder="farm-1" style={{ padding: 10, borderRadius: 10, border: '1px solid #cbd5e1' }} />
            </label>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600, color: '#334155' }}>Production Unit ID</span>
              <input value={productionUnitId} onChange={(event) => setProductionUnitId(event.target.value)} placeholder="pu-1" style={{ padding: 10, borderRadius: 10, border: '1px solid #cbd5e1' }} />
            </label>

            {target === 'productionDatatable' ? (
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ fontWeight: 600, color: '#334155' }}>Species preset</span>
                <select value={speciesKey} onChange={(event) => setSpeciesKey(event.target.value)} style={{ padding: 10, borderRadius: 10, border: '1px solid #cbd5e1' }}>
                  {PRODUCTION_SPECIES_OPTIONS.map((option) => (
                    <option key={option.key} value={option.key}>{option.label}</option>
                  ))}
                </select>
              </label>
            ) : null}

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600, color: '#334155' }}>CSV file</span>
              <input type="file" accept=".csv,text/csv" onChange={handleFileChange} />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                {csvFileStatusText}
              </span>
            </label>

            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ fontWeight: 600, color: '#334155' }}>CSV text</span>
              <textarea
                value={csvText}
                onChange={(event) => setCsvText(event.target.value)}
                rows={12}
                placeholder="Paste CSV text here"
                style={{ padding: 12, borderRadius: 12, border: '1px solid #cbd5e1', resize: 'vertical', fontFamily: 'Consolas, Monaco, monospace' }}
              />
            </label>

            {lotsError ? (
              <div style={{ padding: 12, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
                Lots could not be refreshed for the current context: {lotsError}
              </div>
            ) : null}

            {errorMessage ? (
              <div style={{ padding: 12, borderRadius: 10, background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c' }}>
                {errorMessage}
              </div>
            ) : null}

            {canImportRows ? null : (
              <div style={{ padding: 12, borderRadius: 10, background: '#fff7ed', border: '1px solid #fed7aa', color: '#9a3412' }}>
                Read-only access: this token can review import sessions, but only editor and admin roles can commit rows.
              </div>
            )}

            <button onClick={handleBuildSession} style={{ padding: '12px 16px', borderRadius: 12, border: 'none', background: '#0f172a', color: '#ffffff', fontWeight: 700, cursor: 'pointer' }}>
              Build import session
            </button>
          </div>
        </section>

        <section style={{ display: 'grid', gap: 20 }}>
          <div style={{ ...cardStyle, padding: 20 }}>
            <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.2rem', color: '#0f172a' }}>Current context snapshot</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
              <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Lots</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{lots.length}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>{lotsLoading ? 'Refreshing lots…' : 'Live context lots loaded through useLots().'}</div>
              </div>
              <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Water rows</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{waterRows.length}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>Stored rows available for Water dedupe and review.</div>
              </div>
              <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Production rows</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{productionRows.length}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>Stored production rows available for session building.</div>
              </div>
              <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Feed logs</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{feedRows.length}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>Stored feed logs available for Feed dedupe and review.</div>
              </div>
              <div style={{ padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', color: '#64748b' }}>Mortality logs</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#0f172a' }}>{mortalityRows.length}</div>
                <div style={{ fontSize: 13, color: '#475569' }}>Stored mortality logs available for Mortality dedupe and review.</div>
              </div>
            </div>
          </div>

          <div style={{ ...cardStyle, padding: 20 }}>
            <h2 style={{ marginTop: 0, marginBottom: 16, fontSize: '1.2rem', color: '#0f172a' }}>Session output</h2>
            <ImportWorkspaceSessionOutput
              session={session}
              canImportRows={canImportRows}
              isImportingRows={isImportingRows}
              readyRowCount={readyRowCount}
              onImportRows={handleImportRows}
              onApplyRowCorrection={handleApplyRowCorrection}
              onApplyAllRowCorrections={handleApplyAllRowCorrections}
            />
          </div>
        </section>
      </div>
    </div>
  )
}

const buildBatchSyncResult = (args: {
  rowIds: string[]
  retrySync: (rowIds: string[]) => Promise<EntityBatchSyncStatus>
}): BatchSyncResult => {
  const stableRowIds = Array.from(new Set(args.rowIds.map((rowId) => String(rowId ?? '').trim()).filter(Boolean)))
  if (stableRowIds.length === 0) {
    return {
      syncState: null,
      blockedRows: 0,
      syncErrorMessage: null,
      blockers: [],
      syncPromise: null
    }
  }

  if (typeof navigator === 'undefined' || !navigator.onLine) {
    return {
      syncState: 'offline',
      blockedRows: 0,
      syncErrorMessage: null,
      blockers: [],
      syncPromise: null
    }
  }

  return {
    syncState: 'pending',
    blockedRows: 0,
    syncErrorMessage: null,
    blockers: [],
    syncPromise: args.retrySync(stableRowIds)
  }
}